import moment from 'moment';

export const prettyDateFormat = (oldDateString, separator = '-') => {
	let oldDate = new Date(oldDateString);
	let newDate = '';
	if (oldDate && !isNaN(oldDate)) {
		newDate =
			oldDate.getMonth() +
			1 +
			separator +
			oldDate.getDate() +
			separator +
			oldDate.getFullYear();
	}
	return newDate;
};

export const validateDateString = dateString => {
	if (moment(dateString).isValid()) {
		const now = new Date();
		const y = now.getFullYear();
		const m = now.getMonth();
		const d = now.getDate();
		const before = new Date(y - 100, m, d);
		const later = new Date(y + 100, m, d);
		return (
			isDateStringGreaterThan(later.toString(), dateString) &&
			isDateStringGreaterThan(dateString, before.toString())
		);
	}
};

export const isDateStringGreaterThan = (dateString1, dateString2) => {
	const d1 = new Date(dateString1).setHours(0, 0, 0, 0);
	const d2 = new Date(dateString2).setHours(0, 0, 0, 0);
	return d1 > d2;
};

export const isDateStringInFuture = dateString => {
	const now = new Date().setHours(0, 0, 0, 0);
	return new Date(dateString) > now;
};

export const isDateStringToday = dateString => {
	const now = new Date().setHours(0, 0, 0, 0);
	const target = new Date(dateString + ' 0:0:0:0').setHours(0, 0, 0, 0);
	return target === now;
};

/**
 * Global regular expression pattern evaluating email address validity.
 * @type {string}
 */
export const REGEX_EMAIL = '^[a-zA-Z0-9.!#$%&\'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)+$';

export const isValidEmail = (email) => {
    return validateWithRegex(email, REGEX_EMAIL);
}

export const isValidPhoneNumber = phoneNumber => {
	const phoneRegex = /^\(?([0-9]{3})\)?[-.●]?([0-9]{3})[-.●]?([0-9]{4})$/;
	return validateWithRegex(phoneNumber, phoneRegex);
};

export const isValidZipCode = zip => {
	const zipRegex = /^\d{5}$|^\d{5}-\d{4}$/;
	return validateWithRegex(zip, zipRegex);
};

export const matchPattern = (str, pattern) => {
	return validateWithRegex(str, pattern);
};

export const formatForID = (str) => {
	return str && typeof str !== "undefined" ? String(str).toLowerCase().replace(/[^a-z0-9\s_-]/ig,'').trim().replace(/\s+/g, '-') : '';
};

/**
 * Calculates the Levenshtein distance, or the minimum number of transformations (insertions, deletions, substitutions), between @param source and @param target sequences.
 * @description
 * Uses dynamic programming to construct a matrix of transformation costs, starting with empty subsequences.
 * The Levenshtein Distance algorithm is recursive, considering all combinations of transformations to find the least expensive.
 * To improve efficiency and avoid repeat calculations of the same transformation,
 * a matrix can be constructed to "look back" at previous calculations while performing the current calculation.
 * @see {@link https://en.wikipedia.org/wiki/Levenshtein_distance|Levenshtein Distance}
 * @see {@link https://medium.com/@ethannam/understanding-the-levenshtein-distance-equation-for-beginners-c4285a5604f0|Understanding the Levenshtein Distance Equation for Beginners}
 *
 * @param {string} source starting sequence.
 * @param {string} target desired sequence.
 * @param {boolean} caseSensitive toggle case-sensitive comparisons.
 * @returns {number} minimum number of transformations between inputted sequences.
 */
export const getLevenshteinDistance = (
	source,
	target,
	caseSensitive = false
) => {
	let transformations;

	// impose argument constraints
	if (source === null && target === null) {
		throw new TypeError(
			'Provided arguments are null, at least one non-null value expected.'
		);
	}
	// no transformations
	else if (source === target) {
		transformations = 0;
	}
	// empty source sequence, transformations equal to length of target sequence
	else if (source === null) {
		transformations = target.length();
	}
	// empty target sequence, transformations equal to length of source sequence
	else if (target === null) {
		transformations = source.length();
	}
	// construct matrix storing cost of transformation between all subsequences
	else {
		let matrix = [];

		// initialize with known cost from empty sequence to source (length of source)
		for (let i = 0; i <= source.length; i++) {
			matrix[i] = [i];
		}
		// initialize with known cost from empty sequence to target (length of target)
		for (let j = 0; j <= target.length; j++) {
			matrix[0][j] = j;
		}

		for (let i = 1; i <= source.length; i++) {
			for (let j = 1; j <= target.length; j++) {
				let areEqual = caseSensitive
					? source[i - 1] === target[j - 1]
					: source[i - 1].toLowerCase() ===
					  target[j - 1].toLowerCase();

				matrix[i][j] = areEqual
					? // if characters are same, behave like character does not exist (no additional cost)
					  matrix[i - 1][j - 1]
					: // otherwise, "look back" and increment minimum cost within previous transformations
					  Math.min(
							matrix[i - 1][j], // deletion
							matrix[i][j - 1], // insertion
							matrix[i - 1][j - 1] // substitution
					  ) + 1;
			}
		}
		transformations = matrix[source.length][target.length];
	}

	return transformations;
};

/**
 * Calculates the decimal similarity of @param source to @param target using {@link getLevenshteinDistance}.
 *
 * @param {string} source starting sequence.
 * @param {string} target desired sequence.
 * @param {boolean} caseSensitive toggle case-sensitive comparisons.
 * @returns {number} decimal similarity of inputted sequences.
 */
export const getLevenshteinDecimal = (
	source,
	target,
	caseSensitive = false
) => {
	let decimal = 0;
	if (target !== null) {
		decimal =
			1 -
			getLevenshteinDistance(source, target, caseSensitive) /
				target.length;
	}
	return decimal;
};

const validateWithRegex = (str, regex) => {
	if (str) {
		return str.match(regex) !== null;
	} else {
		return false;
	}
};

//Takes in a CamelCased string and returns Title Cased string
export const TitleCase = str => {
	const result = str.replace(/([A-Z])/g, ' $1');
	const finalResult = result.slice(1);
	return finalResult;
};
